export const surfaceStyles = {
  default: "bg-sand text-burgundy",
  dark: "bg-burgundy text-sand",
  accent: "bg-amaranth text-sand",
  clay: "bg-clay text-sand",
  grain: "bg-millet text-earth",
  earth: "bg-earth text-sand",
} as const;

export type SurfaceTone = keyof typeof surfaceStyles;

export const compatibleCardTones = {
  default: ["dark", "accent", "clay"],
  dark: ["default", "accent", "clay"],
  accent: ["dark", "clay", "default"],
  clay: ["default", "dark", "accent"],
  grain: ["dark", "accent", "earth"],
  earth: ["default", "accent", "clay"],
} as const satisfies Record<SurfaceTone, readonly SurfaceTone[]>;

export const collisionBorderStyles = {
  default: "border-2 border-burgundy",
  dark: "border-2 border-sand",
  accent: "border-2 border-sand",
  clay: "border-2 border-sand",
  grain: "border-2 border-earth",
  earth: "border-2 border-sand",
} as const satisfies Record<SurfaceTone, string>;

export type SurfaceCollisionPolicy = "auto" | "border" | "allow";

export function resolveCardSurface({
  parentTone,
  index = 0,
  requestedTone,
  collisionPolicy = "auto",
}: {
  parentTone: SurfaceTone;
  index?: number;
  requestedTone?: SurfaceTone;
  collisionPolicy?: SurfaceCollisionPolicy;
}) {
  if (!requestedTone) {
    const compatibleTones = compatibleCardTones[parentTone];
    return {
      tone: compatibleTones[index % compatibleTones.length],
      separationClassName: undefined,
    };
  }

  if (requestedTone !== parentTone || collisionPolicy === "allow") {
    return { tone: requestedTone, separationClassName: undefined };
  }

  if (collisionPolicy === "border") {
    return {
      tone: requestedTone,
      separationClassName: collisionBorderStyles[parentTone],
    };
  }

  const compatibleTones = compatibleCardTones[parentTone];
  return {
    tone: compatibleTones[index % compatibleTones.length],
    separationClassName: undefined,
  };
}

export const buttonStyles = {
  primary:
    "rg-button rounded-full border border-burgundy bg-burgundy font-[700] text-sand transition-colors hover:bg-earth disabled:cursor-not-allowed disabled:opacity-45",
  secondary:
    "rounded-full border border-burgundy bg-sand font-[700] text-burgundy transition-colors hover:bg-burgundy hover:text-sand disabled:cursor-not-allowed disabled:opacity-45",
  text: "w-fit border-b-2 border-current pb-1 font-[700]",
} as const;

export type ButtonVariant = keyof typeof buttonStyles;

export const buttonSizeStyles = {
  small: "px-2 py-2 text-xs",
  medium: "px-4 py-3",
  large: "px-8 py-4 text-lg",
} as const;

export type ButtonSize = keyof typeof buttonSizeStyles;

export const cardStyles = {
  frame:
    "flex h-full flex-col overflow-hidden rounded-[2rem] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl",
  bordered:
    "border-2 border-burgundy/20 transition-[transform,box-shadow,border-color] hover:border-burgundy/40",
  width: {
    catalog: "w-full",
    carousel: "w-[71vw] sm:w-[37vw] lg:w-[24vw]",
    related: "w-full",
    compact: "h-[26rem] w-full",
  },
  media: {
    catalog: "aspect-square",
    carousel: "aspect-square",
    related: "h-[22rem]",
    compact: "h-40 shrink-0",
  },
  body: {
    catalog: "flex flex-1 flex-col p-4",
    carousel: "flex h-[19rem] flex-col p-6",
    related: "flex flex-1 flex-col p-6 lg:p-8",
    compact: "flex min-h-0 flex-1 flex-col p-4",
  },
} as const;

export type CardPresentation = keyof typeof cardStyles.width;

export const layoutStyles = {
  page: "min-h-screen px-4 pt-4 pb-10 sm:px-8 lg:px-16 lg:pt-6 lg:pb-16",
  pageContent: "mx-auto w-full",
  section: "pt-12 lg:pt-16",
  followingSection: "pt-16 lg:pt-24",
  sectionInset: "px-6 py-14 lg:px-16 lg:py-20",
  contentMax: "mx-auto max-w-[100rem]",
  catalogGrid:
    "grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5",
  relatedGrid: "grid gap-7 pt-10 md:grid-cols-2 xl:grid-cols-3",
  relatedProductGrid:
    "grid grid-cols-2 gap-4 pt-10 md:grid-cols-3 xl:grid-cols-4",
  wallGrid: "columns-1 gap-7 md:columns-2 xl:columns-3",
  filterLayout:
    "grid gap-10 pt-8 lg:grid-cols-[16rem_1fr] lg:items-start",
  filterOptions:
    "grid grid-cols-2 gap-x-4 gap-y-2 pt-3 lg:grid-cols-1",
} as const;

export const controlStyles = {
  select:
    "mt-2 w-full border border-burgundy/35 bg-sand font-[400] text-burgundy outline-none focus:border-burgundy",
  selectSize: {
    default: "px-3 py-2",
    compact: "px-2 py-1.5 text-sm",
  },
  checkbox: "size-4 accent-burgundy",
} as const;

export const badgeStyles = {
  grain: "rounded-full bg-millet px-3 py-1 font-[700] text-earth",
  story: "rounded-full bg-burgundy px-3 py-1 text-sm font-[700] text-sand",
} as const;

export type BadgeVariant = keyof typeof badgeStyles;

export const storyCardTones = ["dark", "accent", "clay"] as const satisfies readonly SurfaceTone[];
